import { supabase, executeWithTimeout } from '@/lib/supabase';

export const roleService = {
  fetchUserRole: async (userId: string) => {
    // Default role that will be returned if no other role is found
    const defaultRole = {
      roleId: 'user',
      roleName: 'USER'
    };
    
    if (!userId) {
      console.log("[RoleService] No userId provided, returning default role");
      return defaultRole;
    }
    
    try {
      // First, try to get role from JWT token claims
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        console.log("[RoleService] Session data:", sessionData?.session?.user?.app_metadata);
        
        if (sessionData?.session?.user?.app_metadata?.role) {
          const roleName = typeof sessionData.session.user.app_metadata.role === 'string' 
            ? sessionData.session.user.app_metadata.role.toUpperCase() 
            : 'USER';
          
          console.log("[RoleService] Found role in session metadata:", roleName);
          
          return {
            roleId: roleName.toLowerCase(),
            roleName: roleName
          };
        }
      } catch (sessionError) {
        console.error("[RoleService] Error checking session metadata:", sessionError);
      }
      
      // Direct access to user_roles table
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .single();
        
        if (!error && data && data.role) {
          const normalizedRole = typeof data.role === 'string' ? data.role.toUpperCase() : 'USER';
          const roleId = normalizedRole.toLowerCase();
          
          console.log("[RoleService] Role found via direct table query:", normalizedRole);
          
          return {
            roleId,
            roleName: normalizedRole
          };
        }
        
        if (error && error.code !== 'PGRST116') { // Not found error
          console.error("[RoleService] Error querying user_roles table:", error);
        }
      } catch (queryError) {
        console.error("[RoleService] Exception querying user_roles table:", queryError);
      }
      
      // As a fallback, check if user is connected to a dealer that is activated
      try {
        const { data: dealerData, error: dealerError } = await executeWithTimeout(
          async () => {
            const response = await supabase
              .from('dealers')
              .select('activated')
              .eq('user_id', userId)
              .single();
            return response;
          },
          5000
        );
        
        if (!dealerError && dealerData && dealerData.activated) {
          console.log("[RoleService] User has an activated dealer, assigning DEALER role");
          return {
            roleId: 'dealer',
            roleName: 'DEALER'
          };
        }
      } catch (dealerError) {
        console.log("[RoleService] Error checking dealer status:", dealerError);
      }

      // If all else fails, use default role
      console.log("[RoleService] Falling back to default USER role");
      return defaultRole;
    } catch (error) {
      console.error("[RoleService] Critical error in fetchUserRole:", error);
      return defaultRole;
    }
  },
  
  createUserRole: async (userId: string) => {
    console.log("[RoleService] Creating default role for user:", userId);
    
    if (!userId) {
      console.log("[RoleService] No userId provided, skipping role creation");
      return;
    }
    
    try {
      // Instead of directly inserting into user_roles table which may have RLS issues,
      // try to use an RPC function if available
      try {
        const { error } = await supabase.rpc('create_user_role', { 
          user_id_input: userId,
          role_input: 'USER'
        });
        
        if (error) {
          if (error.message.includes('function') && error.message.includes('not found')) {
            console.log("[RoleService] create_user_role RPC not available, trying direct insert");
          } else {
            console.error("[RoleService] Error with create_user_role RPC:", error);
          }
        } else {
          console.log("[RoleService] User role created successfully via RPC");
          return;
        }
      } catch (rpcError) {
        console.log("[RoleService] RPC error in create_user_role:", rpcError);
      }
      
      // Fallback to direct insertion if RPC is not available
      const insertResult = await executeWithTimeout(
        async () => {
          const response = await supabase
            .from('user_roles')
            .insert([
              {
                user_id: userId,
                role: 'USER',
              }
            ]);
          return response;
        },
        5000
      );
      
      if (insertResult.error) {
        if (insertResult.error.code === '42P01') {
          console.log("[RoleService] user_roles table doesn't exist");
        } else {
          console.error("[RoleService] Error creating user role:", insertResult.error);
        }
      } else {
        console.log("[RoleService] User role created successfully via direct insert");
      }
    } catch (roleError) {
      console.error("[RoleService] Exception during role creation:", roleError);
    }
  }
};
